import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { FunnelStep, FunnelStepStatus } from "./types";
import useIsTouchScreen from "./use-is-touch-screen";

interface FunnelSidebarProps {
  steps: FunnelStep[];
  currentStepIndex: number;
  getStepStatus: (index: number) => FunnelStepStatus;
  onStepClick: (index: number) => void;
  lang: "lt" | "en";
}

const statusIcon: Record<FunnelStepStatus, string> = {
  completed: "●",
  in_progress: "◐",
  not_started: "○",
};

const statusIconColor: Record<FunnelStepStatus, string> = {
  completed: "text-green-600",
  in_progress: "text-blue-600",
  not_started: "text-gray-400",
};

const FunnelSidebar: React.FC<FunnelSidebarProps> = ({
  steps,
  currentStepIndex,
  getStepStatus,
  onStepClick,
  lang,
}) => {
  const isTouch = useIsTouchScreen();
  const navRef = useRef<HTMLElement>(null);
  const activeStepRef = useRef<HTMLButtonElement>(null);

  // On a phone the steps are one scrollable row: keep the current step in the
  // middle of it. From md up the list is vertical and this changes nothing.
  useEffect(() => {
    const nav = navRef.current;
    const activeStep = activeStepRef.current;
    if (!nav || !activeStep) return;
    nav.scrollLeft = activeStep.offsetLeft - (nav.clientWidth - activeStep.offsetWidth) / 2;
  }, [currentStepIndex]);

  return (
    <nav
      ref={navRef}
      className="relative flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-x-visible"
    >
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isClickable =
          index <= currentStepIndex || status === "completed";
        const isActive = index === currentStepIndex;

        return (
          <button
            key={step.id}
            ref={isActive ? activeStepRef : undefined}
            className={clsx(
              "flex items-center gap-2 py-2 px-3 border-none text-left rounded-lg w-full whitespace-nowrap md:whitespace-normal",
              isTouch && "min-h-11",
              isActive && "bg-blue-50",
              isClickable ? "cursor-pointer hover:bg-gray-100" : "cursor-default"
            )}
            onClick={() => isClickable && onStepClick(index)}
            type="button"
            disabled={!isClickable}
          >
            <span className={`text-xs shrink-0 ${statusIconColor[status]}`}>
              {statusIcon[status]}
            </span>
            <span
              className={`text-sm ${isActive ? "font-semibold" : "font-normal"}`}
            >
              {`${index + 1}. ${step.title[lang] || step.title.en}`}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default FunnelSidebar;
